import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import apiProvider from "../../apiProvider/api";
import { toast } from "react-toastify";
import InvoiceTemplate2 from "./InvoiceTemplate2";
import { Helmet } from 'react-helmet-async';

const Invoice = () => {
  const { id } = useParams();
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const invoiceRef = useRef(null);
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (id) {
      fetchOrderDetails(id);
    }
  }, [id]);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const invoiceWidth = 590; // Approx width of 148mm plus padding & breathing room in px
        if (containerWidth < invoiceWidth) {
          setScale(containerWidth / invoiceWidth);
        } else {
          setScale(1);
        }
      }
    };

    window.addEventListener("resize", handleResize);
    // Call once initially after data is loaded and rendered
    if (orderData) {
      setTimeout(handleResize, 150);
    }

    return () => window.removeEventListener("resize", handleResize);
  }, [orderData]);

  const fetchOrderDetails = async (orderId) => {
    try {
      setLoading(true);
      const result = await apiProvider.orderDetails(orderId);
      if (result && result.status && result.response) {
        setOrderData(result.response.data);
      } else {
        toast.error("Failed to fetch order details");
      }
    } catch (error) {
      console.error("Error fetching order:", error);
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = async () => {
    if (!invoiceRef.current) return;

    const downloadBtn = document.getElementById("download-btn");
    const printBtn = document.getElementById("print-btn");
    if (downloadBtn) downloadBtn.style.display = "none";
    if (printBtn) printBtn.style.display = "none";

    // Temporarily reset scale to 1.0 for high resolution PDF capture
    setScale(1);
    await new Promise((resolve) => setTimeout(resolve, 150));

    try {
      const canvas = await html2canvas(invoiceRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      pdf.save(`Invoice-${orderData.orderCode || id}.pdf`);
      toast.success("Invoice downloaded successfully");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to download invoice");
    } finally {
      if (downloadBtn) downloadBtn.style.display = "block";
      if (printBtn) printBtn.style.display = "block";

      // Restore responsive scale
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const invoiceWidth = 590;
        if (containerWidth < invoiceWidth) {
          setScale(containerWidth / invoiceWidth);
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!orderData) {
    return <div className="text-center mt-5">Order not found</div>;
  }

  const invoiceHeight = invoiceRef.current ? invoiceRef.current.offsetHeight : 800;

  return (
    <div className="invoice-page-wrapper" style={{ marginTop: '100px', paddingBottom: '50px', backgroundColor: '#f8f9fa' }}>
      <Helmet>
        <title>Invoice | Colours Boutique</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="container">
        <div className="invoice-container shadow-sm p-4 bg-white rounded">
          <div className="d-flex justify-content-end  no-print gap-3">
            <button
              id="download-btn"
              className="btn btn-primary d-flex align-items-center gap-2"
              onClick={handleDownload}
              style={{ backgroundColor: '#582d86', borderColor: '#582d86' }}
            >
              <i className="bi bi-download"></i>
              Download Invoice
            </button>
          </div>
          <div ref={containerRef} className="w-100" style={{ overflow: "hidden", padding: "10px 0", position: "relative" }}>
            <div 
              className="w-100" 
              style={{ 
                height: scale < 1 ? `${invoiceHeight * scale}px` : "auto",
                overflow: "visible",
                position: "relative",
                transition: "height 0.2s ease-in-out",
                minHeight: scale < 1 ? `${invoiceHeight * scale}px` : "auto"
              }}
            >
              <div 
                ref={invoiceRef} 
                style={{ 
                  width: "148mm", 
                  padding: "10px",
                  transform: `scale(${scale})`,
                  transformOrigin: "top center",
                  position: scale < 1 ? "absolute" : "relative",
                  left: scale < 1 ? "50%" : "auto",
                  marginLeft: scale < 1 ? "-74mm" : "auto",
                  transition: "transform 0.2s ease-in-out"
                }}
              >
                <InvoiceTemplate2 orderData={orderData} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            background: white !important;
            margin: 0;
            padding: 0;
          }
          .invoice-page-wrapper {
            padding: 0 !important;
          }
          .invoice-container {
            box-shadow: none !important;
            padding: 0 !important;
          }
          .invoice-container div {
            transform: none !important;
            height: auto !important;
            position: relative !important;
            left: auto !important;
            margin: 0 auto !important;
          }
        }
      `}</style>
    </div>
  );
};


export default Invoice;
